import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FileText, LoaderCircle, X } from "lucide-react";
import { IEpcPayment } from "../../models/epcCustomer.model";
import { getEpcInvoiceId, getEpcInvoiceIds } from "../../utils/epcInvoiceReference";
import InvoicePdfButton from "./InvoicePdfButton";

type EPCInvoiceViewerProps = {
  payment: IEpcPayment;
};

const getInvoiceRecordNumber = (record: unknown): string | undefined => {
  if (!record || typeof record !== "object") return undefined;
  const invoiceNumber = (record as Record<string, unknown>).invoiceNumber;
  return typeof invoiceNumber === "string" || typeof invoiceNumber === "number"
    ? String(invoiceNumber)
    : undefined;
};

const getInvoiceRecordDate = (record: unknown): string => {
  if (!record || typeof record !== "object") return "Date unavailable";
  const createdAt = (record as Record<string, unknown>).createdAt;
  if (typeof createdAt !== "string" && !(createdAt instanceof Date)) {
    return "Date unavailable";
  }
  const date = new Date(createdAt);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString("en-IN")
    : "Date unavailable";
};

const EPCInvoiceViewer: React.FC<EPCInvoiceViewerProps> = ({ payment }) => {
  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [loading, setLoading] = useState(false);
  const rawInvoiceRecords: unknown = payment.invoice?.invoiceId;
  const invoiceRecords = Array.isArray(rawInvoiceRecords)
    ? rawInvoiceRecords
    : rawInvoiceRecords
      ? [rawInvoiceRecords]
      : [];
  const invoiceIds = getEpcInvoiceIds(rawInvoiceRecords);

  const openInvoices = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (invoiceRecords.length > 1) {
      setShowInvoiceList(true);
    }
  };

  if (invoiceRecords.length === 0) return null;

  return (
    <>
      {invoiceRecords.length === 1 ? (
        <InvoicePdfButton
          invoiceId={invoiceIds[0]}
          mode="download"
          invoiceType="EPC"
          fallbackCustomerName={payment.customerName}
          disabled={loading}
          onLoadingChange={setLoading}
          className="inline-flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-slate-300 bg-white px-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {(isLoading) => (
            <>
              {isLoading ? (
                <LoaderCircle
                  size={14}
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <FileText size={14} aria-hidden="true" />
              )}
              View invoice
            </>
          )}
        </InvoicePdfButton>
      ) : (
        <button
          type="button"
          onClick={openInvoices}
          disabled={loading}
          className="inline-flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-slate-300 bg-white px-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? (
            <LoaderCircle
              size={14}
              className="animate-spin"
              aria-hidden="true"
            />
          ) : (
            <FileText size={14} aria-hidden="true" />
          )}
          View invoices
        </button>
      )}

      {showInvoiceList &&
        createPortal(
          <div
            className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/50 p-4"
            onClick={() => {
              if (!loading) setShowInvoiceList(false);
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="epc-invoice-list-title"
              className="flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    EPC invoices
                  </p>
                  <h2
                    id="epc-invoice-list-title"
                    className="mt-1 text-lg font-semibold text-slate-900"
                  >
                    Select an invoice
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Choose an invoice to open its PDF and e-waybill.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close invoice list"
                  disabled={loading}
                  onClick={() => setShowInvoiceList(false)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </header>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                {invoiceRecords.map((invoice, index) => {
                  const invoiceId = getEpcInvoiceId(invoice);
                  return (
                    <InvoicePdfButton
                      key={invoiceId || `invoice-${index}`}
                      invoiceId={invoiceId}
                      mode="download"
                      invoiceType="EPC"
                      fallbackCustomerName={payment.customerName}
                      disabled={loading}
                      onLoadingChange={setLoading}
                      onComplete={() => setShowInvoiceList(false)}
                      className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {(isLoading) => (
                        <>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                            {isLoading ? (
                              <LoaderCircle
                                className="animate-spin"
                                size={18}
                                aria-hidden="true"
                              />
                            ) : (
                              <FileText size={18} aria-hidden="true" />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-slate-900">
                              {getInvoiceRecordNumber(invoice) ||
                                `Invoice ${index + 1}`}
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {getInvoiceRecordDate(invoice)}
                            </span>
                          </span>
                        </>
                      )}
                    </InvoicePdfButton>
                  );
                })}
              </div>
              <footer className="flex justify-start border-t border-slate-100 bg-slate-50 px-5 py-3">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setShowInvoiceList(false)}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200 disabled:opacity-60"
                >
                  Close
                </button>
              </footer>
            </section>
          </div>,
          document.body,
        )}
    </>
  );
};

export default EPCInvoiceViewer;
